import React, { useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, NumberInput, SelectPicker, Toggle, DatePicker } from 'rsuite';
import { AxiosError } from 'axios';
import ErrorMessage from '@/components/ErrorMessage';
import MultilingualInput from '@/components/MultiLingualInput';
import MultilingualTextarea from '@/components/MultiLingualTextarea';
import { multiLingualRule } from '@/utils/multilingual-rule';
import { BaseErrorModel } from '@/models/core.model';
import { EnergyHubOffer, EnergyHubOfferType, EnergyHubOfferTypeList, UpsertEnergyHubOffer } from '@/models/energy-hub.model';
import { createEnergyHubOffer, updateEnergyHubOffer } from '@/services/energy-hub.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const modelSchema = Schema.Model<Record<string, any>>({
  type: Schema.Types.NumberType().isRequired('This field is required.'),
  title: multiLingualRule(200, true),
  terms: multiLingualRule(2000),
  // The API takes a percent only for Discount offers, and requires it there.
  discountPercent: Schema.Types.NumberType().range(1, 100, 'Between 1 and 100.')
    .addRule((value, data) => data.type != EnergyHubOfferType.Discount || (value != null && value !== ''), 'This field is required.', true),
  validTo: Schema.Types.DateType()
    .addRule((value, data) => !value || !data.validFrom || new Date(value) > new Date(data.validFrom), 'Must be after "Valid from".'),
});

const emptyModel: UpsertEnergyHubOffer = {
  type: EnergyHubOfferType.FreeExtra,
  title: {},
  isActive: true,
};

interface Props extends DrawerProps {
  energyHubId: number;
  offer?: EnergyHubOffer;
  onSubmitted?: () => void;
}

const OfferDrawer = (props: Props) => {
  const { open, onClose, onSubmitted, energyHubId, offer, ...rest } = props;
  const [formValue, setFormValue] = useState<UpsertEnergyHubOffer>({ ...emptyModel });
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  React.useEffect(() => {
    if (!open) return;
    setErrorModel(undefined);
    setFormValue(offer ? {
      type: offer.type,
      title: offer.title ?? {},
      terms: offer.terms ?? {},
      discountPercent: offer.discountPercent,
      isActive: offer.isActive,
      validFrom: offer.validFrom,
      validTo: offer.validTo,
    } : { ...emptyModel });
  }, [open, offer]);

  const handleClose = (event?: React.SyntheticEvent) => {
    setLoading(false);
    onClose?.(event as any);
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) return;
    const isDiscount = formValue.type == EnergyHubOfferType.Discount;
    const body: UpsertEnergyHubOffer = {
      ...formValue,
      discountPercent: isDiscount ? Number(formValue.discountPercent) : undefined,
      validFrom: formValue.validFrom ?? undefined,
      validTo: formValue.validTo ?? undefined,
    };
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = offer
        ? await updateEnergyHubOffer(energyHubId, offer.id, body)
        : await createEnergyHubOffer(energyHubId, body);
      if (result.success) {
        toastSuccessMessage('Offer saved');
        handleClose();
        onSubmitted?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" open={open} onClose={handleClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>{offer ? 'Edit Offer' : 'New Offer'}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">Save</Button>
          <Button onClick={handleClose} appearance="subtle">Cancel</Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        <Form name="energy-hub-offer-form" fluid ref={formRef}
          onChange={value => setFormValue(pre => ({ ...pre, ...value } as UpsertEnergyHubOffer))}
          formValue={formValue} model={modelSchema} disabled={loading}
        >
          <Form.Group>
            <Form.Label>Type</Form.Label>
            <Form.Control name="type" accepter={SelectPicker} data={EnergyHubOfferTypeList} searchable={false} cleanable={false} block />
          </Form.Group>
          {formValue.type == EnergyHubOfferType.Discount && (
            <Form.Group>
              <Form.Label>Discount %</Form.Label>
              <Form.Control name="discountPercent" accepter={NumberInput} min={1} max={100} />
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control name="title" accepter={MultilingualInput} />
            <Form.HelpText>e.g. "Free extra espresso shot". Pick a language and type its text; add as many languages as you need.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Terms</Form.Label>
            <Form.Control name="terms" accepter={MultilingualTextarea} rows={3} />
            <Form.HelpText>e.g. "One per runner per visit. Show the app at the counter."</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Valid from (optional)</Form.Label>
            <Form.Control name="validFrom" accepter={DatePicker} format="yyyy-MM-dd HH:mm" placement="autoVerticalStart" block />
          </Form.Group>
          <Form.Group>
            <Form.Label>Valid to (optional)</Form.Label>
            <Form.Control name="validTo" accepter={DatePicker} format="yyyy-MM-dd HH:mm" placement="autoVerticalStart" block />
          </Form.Group>
          <Form.Group>
            <Form.Label>Active</Form.Label>
            <Form.Control name="isActive" accepter={Toggle} />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      </Drawer.Body>
    </Drawer>
  );
};

export default OfferDrawer;
